import * as React from 'react';
import ConnectedWidget from './index';
import { ThemeConfig } from './components/Widget/ThemeContext';

type Props = {
    children: React.ReactNode;
};

class ErrorBoundary extends React.Component<Props> {
    state: any;

    constructor(props: any) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: any) {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        // You can also log the error to an error reporting service
        console.error('caught chat widget error', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return null;
        }

        return this.props.children;
    }
}

export type BotifyChatWidgetProps = {
    getToken?: () => Promise<string>;
    getTokenIntervalMs?: number;
    invalidateToken?: () => Promise<void>;
    protocol?: string;
    socketUrl?: string;
    socketPath?: string;
    httpBackendUrl?: string;
    browserLocation?: { pathname: string };
    navigateCallbacks?: { [callbackName: string]: Function };
    onWidgetEvent?: { [callbackName: string]: Function };
    jovoWebhookUrl?: string;
    title?: string;
    subtitle?: string;
    inputTextFieldHint?: string;
    connectingText?: string;
    welcomeMessages?: string[];
    launchIntent?: boolean;
    profileAvatar?: string;
    loglevel?: string;
    storage?: string;
    styleTheme?: ThemeConfig;
    launcherGreetingsDelayMs?: number;
    fetchTasksRetryCount?: number;
    locationChangeEventsActiveSeconds?: number;
    deeplink?: string;
    userInfo?: {
        customerId?: string;
        userId?: string;
    };
    partnerId?: string;
    /**
     * When true (and not `embedded`), the launcher opens the conversation as a
     * centered modal over a dimmed, click-to-close backdrop instead of the
     * default bottom-right floating window. Baked into the adj variant.
     */
    overlayMode?: boolean;
    /**
     * Non-embedded chat window size, expressed as the distance from the
     * viewport edges — the window fills what's left (centered in overlay mode).
     * e.g. chatWindowInsetX: '8vw', chatWindowInsetY: '6vh' (any CSS length).
     * Desktop only: below the mobile breakpoint the chat stays full-screen.
     */
    chatWindowInsetX?: string;
    chatWindowInsetY?: string;
    /**
     * Hide the message-preview bubbles on the closed launcher — both the
     * last-bot-message tooltip and the proactive greeting bubble.
     */
    disablePreviewBubble?: boolean;
    /** Host page URL when the widget runs inside the iframe loader; replaces window.location for URL tracking. */
    pageUrl?: string;
    /** Referrer to report on session_start when the widget runs inside the iframe loader. */
    analyticsReferrer?: string;
    /**
     * Embedded mode on mobile (≤ 800px): focusing the composer moves the chat
     * into a fullscreen overlay sized to the visible viewport, so the input
     * stays above the on-screen keyboard. Default true; set false to keep the
     * chat inline (e.g. a host page that already is a dedicated chat screen).
     */
    mobileFullScreenOnFocus?: boolean;
};

export type CUIChatWidgetHandle = { open: () => void; close: () => void; toggle: () => void };

export const CUIChatWidget = React.forwardRef<CUIChatWidgetHandle, BotifyChatWidgetProps>(
    function CUIChatWidget(props, ref) {
        const { storage, ...restProps } = props;

        // NOTE: to make this compatible with widget's PropTypes, we explicitly
        //   cast the props to any here before injecting them into ConnectedWidget:
        const connectedProps: any = {
            connectOn: 'open',
            hideWhenNotConnected: false,
            params: { storage: storage || 'session' },
            ...restProps,
        };

        return (
            <ErrorBoundary>
                <ConnectedWidget ref={ref} {...connectedProps} />
            </ErrorBoundary>
        );
    }
);
